import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MediaChartByType } from "@/components/feature/media/media-chart-by-type";
import { useOutletKinds } from "@/hooks/use-outlet-kinds";
import { media, AD_TYPE_LABELS } from "@/lib/api";
import { useActiveClient } from "@/hooks/use-active-client";

export const Route = createFileRoute("/_auth/media/")({
  component: MediaDashboard,
});

function MediaDashboard() {
  const { labels: kindLabels } = useOutletKinds();
  const { activeClientId } = useActiveClient();
  const cid = activeClientId ?? undefined;
  // Clientes y medios son catálogos: quedan sin filtrar; el resto respeta el cliente activo.
  const clients = useQuery({ queryKey: ["media", "clients"], queryFn: () => media.listClients({ limit: 200 }) });
  const campaigns = useQuery({ queryKey: ["media", "campaigns", activeClientId], queryFn: () => media.listCampaigns({ limit: 200, client_id: cid }) });
  const placements = useQuery({ queryKey: ["media", "placements", activeClientId], queryFn: () => media.listPlacements({ limit: 500, client_id: cid }) });
  const outlets = useQuery({ queryKey: ["media", "outlets"], queryFn: () => media.listOutlets({ limit: 500 }) });
  const ads = useQuery({ queryKey: ["media", "ads", activeClientId], queryFn: () => media.listAds({ limit: 500, client_id: cid }) });
  const activeClientName = activeClientId != null
    ? (clients.data ?? []).find(c => c.id === activeClientId)?.name ?? `#${activeClientId}`
    : null;

  const loading = clients.isLoading || campaigns.isLoading || placements.isLoading || outlets.isLoading || ads.isLoading;
  if (loading) return <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;

  // Agregaciones
  const byState: Record<string, number> = {};
  const byCoverage: Record<string, number> = {};
  for (const p of placements.data ?? []) {
    byState[p.state] = (byState[p.state] || 0) + 1;
    byCoverage[p.coverage] = (byCoverage[p.coverage] || 0) + 1;
  }

  const byAdType: Record<string, number> = {};
  for (const a of ads.data ?? []) {
    byAdType[a.ad_type] = (byAdType[a.ad_type] || 0) + 1;
  }

  const byOutletKind: Record<string, number> = {};
  const outletById = new Map((outlets.data ?? []).map(o => [o.id, o]));
  for (const p of placements.data ?? []) {
    const o = outletById.get(p.outlet_id);
    if (o) byOutletKind[o.kind] = (byOutletKind[o.kind] || 0) + 1;
  }

  return (
    <>
      {activeClientName && (
        <div className="text-xs text-ink-500 mb-3">
          Filtrado por: <span className="font-medium text-ink-700 dark:text-ink-200">{activeClientName}</span>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Clientes" value={(clients.data ?? []).filter(c => c.is_active).length} />
        <Kpi label="Campañas activas" value={(campaigns.data ?? []).filter(c => c.status === "activa").length} />
        <Kpi label="Placements" value={(placements.data ?? []).length} />
        <Kpi label="Medios catálogo" value={(outlets.data ?? []).length} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat color="bg-amber-100 text-amber-800" label="Programado" value={byState.programado || 0} />
        <Stat color="bg-blue-100 text-blue-800" label="Finalizado" value={byState.finalizado || 0} />
        <Stat color="bg-emerald-100 text-emerald-800" label="Realizado" value={byState.realizado || 0} />
        <Stat color="bg-rose-100 text-rose-800" label="Cancelado" value={byState.cancelado || 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MediaChartByType
          title="Placements por tipo de aviso"
          data={byAdType}
          labels={AD_TYPE_LABELS as Record<string, string>}
        />
        <MediaChartByType
          title="Placements por tipo de medio"
          data={byOutletKind}
          labels={kindLabels}
        />
        <MediaChartByType
          title="Por cobertura"
          data={byCoverage}
        />
        <MediaChartByType
          title="Por estado"
          data={byState}
        />
      </div>
    </>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="py-5">
        <div className="text-xs text-ink-500 uppercase tracking-wide">{label}</div>
        <div className="text-3xl font-bold text-ink-900 dark:text-white">{value.toLocaleString("es-PE")}</div>
      </CardContent>
    </Card>
  );
}

function Stat({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <div className={`rounded-lg px-3 py-2 ${color}`}>
      <div className="text-xs">{label}</div>
      <div className="text-xl font-semibold">{value.toLocaleString("es-PE")}</div>
    </div>
  );
}
